import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, FC, useMemo } from 'react';
import styled from 'styled-components';

import FieldSchemaSelect from 'dashboard/components/FieldSchemaSelect';
import { Option } from 'dashboard/components/JsonSchemaSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  filterUsableObjects,
  REPORT_OBJECT_KEYS,
  snakify,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import {
  WorkflowError,
  WorkflowFieldKey,
  WorkflowFieldObjectKey,
  WorkflowStatus,
} from 'dashboard/models/workflow';
import {
  WorkflowActionScheduleWorkflowRunV1,
  WorkflowTimeUnit,
} from 'dashboard/models/workflow-version';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import usePermission from 'lib/hooks/usePermission';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const TimeSettingsRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 28% 35% 32% 5%;
  column-gap: ${Spacing[2]};
`;

enum When {
  Before = 'before',
  After = 'after',
}

const beforeOrAfterOptions = [
  {
    label: 'Before',
    value: When.Before,
  },
  {
    label: 'After',
    value: When.After,
  },
];

const unitOptions = [
  {
    label: 'Minutes',
    value: WorkflowTimeUnit.Minutes,
  },
  {
    label: 'Hours',
    value: WorkflowTimeUnit.Hours,
  },
  {
    label: 'Days',
    value: WorkflowTimeUnit.Days,
  },
  {
    label: 'Weeks',
    value: WorkflowTimeUnit.Weeks,
  },
  {
    label: 'Months',
    value: WorkflowTimeUnit.Months,
  },
  {
    label: 'Years',
    value: WorkflowTimeUnit.Years,
  },
];

const PERMITTED_OBJECTS = [
  WorkflowFieldObjectKey.Inquiry,
  WorkflowFieldObjectKey.Account,
  WorkflowFieldObjectKey.Case,
  WorkflowFieldObjectKey.Transaction,
  ...REPORT_OBJECT_KEYS,
];

interface ParamsScheduleWorkflowRunV1Props extends ParamsBaseProps {
  workflows: { id: string; name: string; 'trigger-type': string; status: string }[];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionScheduleWorkflowRunV1['params'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionScheduleWorkflowRunV1['params']) => void;
}

const ParamsScheduleWorkflowRunV1: FC<ParamsScheduleWorkflowRunV1Props> = ({
  workflows,
  editable,
  errors,
  params,
  usableObjects,
  updateActionParams,
  step,
  updateStep,
}) => {
  const hasWorkflowPerm = usePermission(Permission.WorkflowView);
  const isTemplateAvailable = useMemo(() => {
    return !params['workflow-id'] || workflows.map((wf) => wf.id).includes(params['workflow-id']);
  }, [params, workflows]);
  const availableWorkflows = useMemo(() => {
    return (
      workflows
        ?.filter(
          (workflow) =>
            workflow['trigger-type'] === ApiModel.TriggerSchedule &&
            workflow['status'] === WorkflowStatus.Running,
        )
        .map((workflow) => ({
          label: workflow.name,
          value: workflow.id,
        })) || []
    );
  }, [workflows]);

  const onSelectObject = (object: WorkflowActionScheduleWorkflowRunV1['params']['object']) => {
    updateActionParams({
      ...params,
      object,
    });
  };

  const onSelectTimeOffset = (
    timeOffset: WorkflowActionScheduleWorkflowRunV1['params']['time-offset'],
  ) => {
    updateActionParams({
      ...params,
      'time-offset': timeOffset,
    });
  };

  const onSelectUnit = (unit: WorkflowActionScheduleWorkflowRunV1['params']['time-unit']) => {
    updateActionParams({
      ...params,
      'time-unit': unit,
    });
  };

  const onSelectBeforeOrAfter = (value: When) => {
    updateActionParams({
      ...params,
      'time-offset': Math.abs(params['time-offset'] || 0) * (value === When.Before ? -1 : 1),
    });
  };

  const onSelectWorkflow = (
    workflowId: WorkflowActionScheduleWorkflowRunV1['params']['workflow-id'],
  ) => {
    const workflowName = availableWorkflows.find((wf) => wf.value === workflowId)?.label;
    updateStep({
      ...step,
      name: `Schedule Workflow: ${workflowName}`,
      action: {
        ...(step.action as WorkflowActionScheduleWorkflowRunV1),
        params: {
          ...params,
          'workflow-id': workflowId,
        },
      },
    });
  };

  const onSetReferenceDate = (option: Option) => {
    updateActionParams({
      ...params,
      'input-fields': {
        ...params['input-fields'],
        'reference-datetime': option.value,
      },
    });
    ('');
  };

  const handleChangeUseCurrentDateAsReference = ({
    target: { checked },
  }: ChangeEvent<HTMLInputElement>) => {
    const { 'reference-datetime': _, ...inputFields } = params['input-fields'];
    updateActionParams({
      ...params,
      'input-fields': {
        ...inputFields,
        ...(checked ? {} : { 'reference-datetime': '' }),
      },
    });
  };

  // indicates if the config uses a provided reference date time instead of the default time of the current run
  const configReferenceDateTime = params['input-fields']['reference-datetime'] != null;

  const filteredUsableObjects = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: PERMITTED_OBJECTS,
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const allowedFieldPaths = useMemo(() => {
    return filteredUsableObjects.map((usableObject) => {
      const { value, workflowObjectKey } = usableObject;
      const fieldPath =
        workflowObjectKey === WorkflowFieldObjectKey.Inquiry
          ? `${value}.${WorkflowFieldKey.Fields}`
          : value;
      return snakify(fieldPath);
    });
  }, [filteredUsableObjects]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionScheduleWorkflowRunV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Workflow'
        htmlFor='params-run-workflow__workflow-input'
        helpText={
          hasWorkflowPerm
            ? isTemplateAvailable
              ? undefined
              : 'The current workflow is not available to you at this time.'
            : 'Your role does not have permissions to view workflows.'
        }
      />
      <ReactSelect
        value={params['workflow-id']}
        options={availableWorkflows}
        onChange={onSelectWorkflow}
        id='params-run-workflow__workflow'
        disabled={!editable || !hasWorkflowPerm}
        placeholder={
          hasWorkflowPerm ? (isTemplateAvailable ? undefined : 'UNAVAILABLE') : 'RESTRICTED'
        }
        hasError={hasErrorFor('workflow-id')}
      />
      {renderErrorMessageFor('workflow-id')}
      <Spacer height={2} />
      <ParamLabel labelText='Object' htmlFor='params-run-workflow__object-input' />
      <ReactSelect
        value={params['object']}
        options={filteredUsableObjects}
        onChange={onSelectObject}
        id='params-run-workflow__object'
        disabled={!editable}
        hasError={hasErrorFor('object')}
      />
      {renderErrorMessageFor('object')}
      <Spacer height={2} />
      <TimeSettingsRow>
        <div>
          <ParamLabel labelText='Offset' htmlFor='params-run-workflow__offset' />
          <Input
            id='params-run-workflow__offset'
            type='number'
            min='0'
            value={params['time-offset'] ? Math.abs(params['time-offset'] as number) : 0}
            onChange={(e) => onSelectTimeOffset(Number(e.target.value))}
            disabled={!editable}
          />
        </div>
        <div>
          <ParamLabel labelText='Units' htmlFor='params-run-workflow__units-input' />
          <ReactSelect
            value={params['time-unit']}
            options={unitOptions}
            onChange={onSelectUnit}
            id='params-run-workflow__units'
            disabled={!editable}
            hasError={hasErrorFor('time-unit')}
          />
        </div>
        <div>
          <ParamLabel labelText='Before/After' htmlFor='params-run-workflow__before-after-input' />
          <ReactSelect
            value={
              !configReferenceDateTime
                ? When.After
                : params['time-offset'] < 0
                  ? When.Before
                  : When.After
            }
            options={
              !configReferenceDateTime
                ? beforeOrAfterOptions.filter((one) => one.value === When.After)
                : beforeOrAfterOptions
            }
            onChange={onSelectBeforeOrAfter}
            id='params-run-workflow__before-after'
            disabled={!editable}
            hasError={hasErrorFor('time-offset')}
          />
        </div>
      </TimeSettingsRow>
      <Spacer height={2} />
      <ParamLabel labelText='Reference date' htmlFor='params-run-workflow__reference-date' />
      <div>
        <Checkbox
          id='params-run-workflow__reference-date'
          checked={!configReferenceDateTime}
          onChange={handleChangeUseCurrentDateAsReference}
          label='Time of this workflow run'
        />
      </div>
      {configReferenceDateTime && (
        <>
          <Spacer height={2} />
          <FieldSchemaSelect
            initialValue={params['input-fields']['reference-datetime'] || ''}
            usableObjects={filteredUsableObjects}
            allowedFieldPaths={allowedFieldPaths}
            onFieldSelect={(option) => onSetReferenceDate(option)}
            size='sm'
            hasError={hasErrorFor('input-fields')}
          />
        </>
      )}
    </>
  );
};

export default ParamsScheduleWorkflowRunV1;
